<script lang="ts">
  /**
   * The Categories cell: one chip per *category*, not per raw DLP info type (spec 6.3).
   *
   * Categories and colours are the Sensitive Data Detection page's own `categoryMeta`, so the two
   * pages agree; every raw type that page does not show lands in one explicit "Uncategorised"
   * chip, which always sorts last. A chip carries the category's summed count; its tooltip lists
   * the category's detectors by Google display name, each with its own count (#2250).
   *
   * The cut is a hard three chips plus "+N more", not a measured one-line fit: the cell is a
   * fixed grid column, so three is what fits. Measured overflow belongs to the filter bar's
   * chips (#2206), which wrap across a full-width row.
   */
  import { Tooltip, TooltipContent, TooltipTrigger } from "$lib/ui/tooltip";

  import { categoryColour, type NamedCustomInfoType } from "../categoryLabels";
  import { rollUpInfoTypes } from "../exposedSensitiveDocuments";

  let {
    counts,
    limit = 3,
    customInfoTypes = [],
  }: {
    counts: Record<string, number> | undefined;
    /** Chips shown before the "+N more" overflow. */
    limit?: number;
    /**
     * The org's enabled custom info types; each is its own category rather than Uncategorised.
     * Omitted, every custom name falls through to Uncategorised.
     */
    customInfoTypes?: NamedCustomInfoType[];
  } = $props();

  const rollUp = $derived(rollUpInfoTypes(counts, { customInfoTypes }));
  const shown = $derived(rollUp.slice(0, limit));
  const overflow = $derived(rollUp.slice(limit));
  const overflowTitle = $derived(overflow.map((category) => `${category.label} (${category.count})`).join(", "));
</script>

<span class="flex flex-wrap items-center gap-0.5 min-w-0">
  {#each shown as category (category.category)}
    <Tooltip>
      <TooltipTrigger>
        <span
          class="inline-flex items-center gap-1 rounded-full border border-black/10 px-1.5 py-0 text-[10px] font-medium text-offblack whitespace-nowrap"
          style="background:{categoryColour(category)}"
        >
          {category.label}
          <span class="rounded bg-white/70 px-1 font-semibold tabular-nums">{category.count}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>
        <div class="flex flex-col gap-0.5 text-xs">
          {#each category.detectors as detector (detector.infoType)}
            <span data-testid="category-chip-detector">{detector.displayName} · {detector.count}</span>
          {/each}
        </div>
      </TooltipContent>
    </Tooltip>
  {/each}
  {#if overflow.length > 0}
    <span class="text-[10px] text-gray-500 whitespace-nowrap" title={overflowTitle}>+{overflow.length} more</span>
  {/if}
</span>
